import React, { useEffect, useRef, useState } from "react";

/**
 * Wraps content and fades/slides it in when it scrolls into view.
 * @param {Object} props
 * @param {React.ReactNode} props.children - Content to reveal
 * @param {string} [props.className] - Extra class names
 * @param {boolean} [props.stagger] - Stagger animation for direct children
 * @param {number} [props.threshold=0.1] - How much of the element must be visible (0–1)
 * @param {string} [props.rootMargin='0px 0px -40px 0px'] - Margin around viewport for triggering
 */
function ScrollReveal({ children, className = "", stagger = false, threshold = 0.1, rootMargin = "0px 0px -40px 0px" }) {
  const ref = useRef(null);
  const [hasRevealed, setHasRevealed] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const revealIfVisible = () => {
      const rect = el.getBoundingClientRect();
      if (rect.top < window.innerHeight && rect.bottom > 0) {
        setHasRevealed(true);
      }
    };

    revealIfVisible();

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) setHasRevealed(true);
        });
      },
      { threshold, rootMargin }
    );

    observer.observe(el);
    window.addEventListener("scroll", revealIfVisible, { passive: true });
    return () => {
      observer.disconnect();
      window.removeEventListener("scroll", revealIfVisible);
    };
  }, [threshold, rootMargin]);

  const revealClass = [
    "scroll-reveal",
    hasRevealed ? "scroll-reveal--visible" : "",
    stagger ? "scroll-reveal--stagger" : "",
    className
  ].filter(Boolean).join(" ");

  return (
    <div ref={ref} className={revealClass}>
      {children}
    </div>
  );
}

export default ScrollReveal;
